import { motion } from "framer-motion";
import Footer from "../components/Footer";
import MarketingNav from "../components/layout/MarketingNav";
import {
  CONTACT_ADDRESS_LINE,
  CONTACT_EMAIL,
  CONTACT_PHONE,
} from "@/data/siteContact";

const LAST_UPDATED = "22 August 2026";

type Section = { title: string; paragraphs: string[] };

const sections: Section[] = [
  {
    title: "1. Introduction",
    paragraphs: [
      "Skutopia Academy (“Skutopia”, “we”, “us”) operates the Skutopia Academy mobile application and website at skutopia.academy. This Privacy Policy explains how we collect, use, disclose, and protect personal information when you use our services.",
      "By creating an account or using Skutopia Academy, you agree to this Privacy Policy. If you do not agree, please do not use our services.",
    ],
  },
  {
    title: "2. Information we collect",
    paragraphs: [
      "Account information: name, email address, password (stored securely), profile photo, school, grade, subjects, and onboarding preferences.",
      "Learning data: course progress, quiz and flashcard results, XP, study activity, and interactions with AI learning assistants (Muzanga).",
      "Social and discovery data: friend connections, chat messages, student stories, opportunity applications, and partner messages when you use those features.",
      "Device and technical data: device type, operating system, app version, push notification tokens (FCM), crash logs, and analytics events to keep the app reliable and secure.",
      "Optional content: photos, documents, or attachments you choose to upload (for example profile pictures, chat attachments, or admissions documents).",
    ],
  },
  {
    title: "3. How we use your information",
    paragraphs: [
      "We use personal information to provide and improve learning features, personalize content, track progress, recommend opportunities, deliver notifications, prevent abuse, and respond to support requests.",
      "AI features may use relevant learning context (such as grade, subject, and recent activity) to generate helpful responses. We do not use your data to train public third-party AI models without your consent.",
    ],
  },
  {
    title: "4. Legal bases (where applicable)",
    paragraphs: [
      "We process personal data to perform our contract with you (providing the app), with your consent (for example guardian approval for under-16 social features), and where necessary for legitimate interests such as security, analytics, and product improvement.",
    ],
  },
  {
    title: "5. Children and guardian consent",
    paragraphs: [
      "Skutopia Academy is designed for secondary school learners. If you are under 16, a parent or guardian must approve social features before chat, friends, and similar tools are unlocked. Learning features may remain available while consent is pending.",
      "We collect guardian contact details only to manage consent and communicate about account safety when required.",
    ],
  },
  {
    title: "6. Sharing and service providers",
    paragraphs: [
      "We do not sell your personal information.",
      "We use trusted processors to operate the platform, including Supabase (database and authentication), Firebase/Google Cloud (push notifications, analytics, crash reporting), and cloud storage providers for media you upload. These providers process data on our behalf under contractual safeguards.",
      "Partner institutions may receive information you submit in opportunity or admissions applications. Messages you send to partners through Discover are shared with the relevant partner organization.",
    ],
  },
  {
    title: "7. Photos, camera, and notifications",
    paragraphs: [
      "When you attach a profile photo or send images in chat, the app uses the Android system photo picker or camera where available. We do not request broad access to all photos or videos on your device.",
      "Push notifications require your device permission and are used for messages, learning updates, and announcements. You can disable notifications in device settings.",
    ],
  },
  {
    title: "8. Data retention",
    paragraphs: [
      "We retain personal information while your account is active and as needed to provide services, comply with law, resolve disputes, and maintain security. You may request deletion of your account through in-app Support.",
    ],
  },
  {
    title: "9. Security",
    paragraphs: [
      "We use industry-standard measures including encrypted connections (HTTPS/TLS), access controls, and secure authentication. No method of transmission or storage is 100% secure; we work continuously to protect your data.",
    ],
  },
  {
    title: "10. Your rights and choices",
    paragraphs: [
      "Depending on your location, you may have rights to access, correct, delete, or restrict processing of your personal data, or to withdraw consent where processing is consent-based.",
      "You can update profile information in the app, manage notification preferences on your device, and contact us to exercise privacy rights.",
    ],
  },
  {
    title: "11. International transfers",
    paragraphs: [
      "Your information may be processed in countries where our service providers operate. We take steps to ensure appropriate safeguards when data is transferred internationally.",
    ],
  },
  {
    title: "12. Changes to this policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time. We will post the revised policy on this page with an updated date. Continued use after changes means you accept the updated policy.",
    ],
  },
  {
    title: "13. Contact us",
    paragraphs: [
      `${CONTACT_ADDRESS_LINE}. Email: ${CONTACT_EMAIL}. Phone: ${CONTACT_PHONE}. For in-app support, use the Support section in the Skutopia Academy app.`,
    ],
  },
];

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-white flex flex-col">
    <MarketingNav />

    <article className="py-10 sm:py-14 flex-1">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.header
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500">Last updated: {LAST_UPDATED}</p>
          <p className="mt-4 text-gray-700 leading-relaxed">
            This policy applies to the Skutopia Academy mobile application and related
            services operated by Skutopia Academy.
          </p>
        </motion.header>

        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.section
              key={section.title}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.04 }}
            >
              <h2 className="text-lg font-semibold text-skutopia-700 mb-3">
                {section.title}
              </h2>
              <div className="space-y-3">
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph.slice(0, 40)} className="text-gray-700 text-sm leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.section>
          ))}
        </div>
      </div>
    </article>

    <Footer />
  </div>
);

export default PrivacyPolicy;
